import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { savingsService } from "../../../../../shared/core/service/services";
import type { Account } from "../../../../../shared/domains/account/account";
import type { TaxStatement } from "../../../../../shared/domains/savings/savings";
import { RoundedSquareButton } from "../../../common/shape/rounded-square";
import { DownloadIcon } from "../../../common/svg/download-icon";
import { theme } from "../../../styles/theme";

interface TaxStatementRowProps extends HTMLAttributes<HTMLDivElement> {
  statement: TaxStatement;
  account: Account;
  withAccountId?: boolean;
  variant?: "normal" | "fee";
}

export const TaxStatementRow = (props: TaxStatementRowProps) => {
  const { statement, account, variant = "normal", withAccountId, ...rest } = props;
  const { formatMessage, formatDate } = useIntl();
  const firstDayOfYear = new Date(Number(statement.year), 0, 1);
  const lastDayOfYear = new Date(Number(statement.year), 11, 31);
  return (
    <Row {...rest}>
      <Column>
        <Title>
          {formatDate(firstDayOfYear, { year: "numeric", month: "long", day: "numeric" })} -{" "}
          {formatDate(lastDayOfYear, { year: "numeric", month: "long", day: "numeric" })}
        </Title>
        {withAccountId && <Value>{formatMessage("taxStatements.accountLabel", { id: account.id })}</Value>}
      </Column>
      <GreyRoundedSquareButton size={34} onClick={() => savingsService.downloadTaxStatement(statement.documentId)}>
        <DownloadIcon />
      </GreyRoundedSquareButton>
    </Row>
  );
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-self: stretch;
  justify-content: space-between;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  ${theme.boldText};
  font-size: 0.9375rem;
`;

const Value = styled.span`
  ${theme.text};
  color: #b1b1b1;
  font-size: 0.9375rem;
`;

const GreyRoundedSquareButton = styled(RoundedSquareButton)`
  background-color: #f4f4f4;
`;
